import { ref } from 'vue'
import axiosInstance from '@/axios'

/**
 * useStorefrontSearch -- fetch storefront product search results
 * (`GET /api/storefront/<slug>/search?q=...`).
 *
 * Card: Storefront browse (https://trello.com/c/HUIrLhaU), pebble "Search".
 *
 * Shared by BOTH search surfaces so they can never disagree about what
 * matches: the combo-box suggestion list (a small `per_page`) and the
 * results page (the full paginated set). A second fetcher for the dropdown
 * would be a second definition of "what does this store match", and the two
 * would drift.
 *
 * Payload (from ``storefront_search``):
 *   { products: [<_product_summary>], total, page, per_page, query }
 *
 * ``query`` is the server's trimmed/truncated echo of the term. Callers
 * render it as TEXT (it is reflected user input); nothing here builds
 * markup from it.
 *
 * Behavior mirrors useStorefrontCategory: concurrent fetches are de-duped
 * via a generation counter so a slow EARLIER response can never overwrite a
 * newer one -- the ordering hazard that matters most for type-ahead, where
 * requests are issued per keystroke and routinely resolve out of order.
 *
 * A BLANK or whitespace-only term short-circuits WITHOUT a request: the
 * shopper has not asked for anything, and an empty `q` would otherwise be a
 * needless round-trip on every field-clear.
 *
 * Public surface:
 *   const { products, total, query, loading, error, search } = useStorefrontSearch()
 */
export function useStorefrontSearch() {
    const products = ref([])
    const total = ref(0)
    // The server's echo of the term, for the results-page title/breadcrumb.
    const query = ref('')
    const loading = ref(false)
    const error = ref(null)

    let generation = 0

    /**
     * Run a search.
     *
     * @param {string} slug     store slug
     * @param {string} term     raw search term (trimmed here)
     * @param {object} options  { perPage, page }
     */
    const search = async (slug, term, options = {}) => {
        const trimmed = typeof term === 'string' ? term.trim() : ''

        if (!slug || !trimmed) {
            // Not an error state -- simply nothing asked for. Clear results
            // so a cleared field cannot leave the previous matches on screen.
            generation += 1
            products.value = []
            total.value = 0
            query.value = ''
            error.value = null
            loading.value = false
            return
        }

        const myGeneration = ++generation

        loading.value = true
        error.value = null

        const params = new URLSearchParams({ q: trimmed })
        if (options.perPage) params.set('per_page', String(options.perPage))
        if (options.page) params.set('page', String(options.page))

        const url =
            `/api/storefront/${encodeURIComponent(slug)}/search?${params.toString()}`

        try {
            const response = await axiosInstance.get(url)

            // A STALE response: a newer search started while this one was in
            // flight. Discard it -- applying it would show results for a
            // prefix the shopper has already typed past.
            if (myGeneration !== generation) return

            const data = response.data || {}
            products.value = Array.isArray(data.products) ? data.products : []
            total.value = Number(data.total) || 0
            query.value = typeof data.query === 'string' ? data.query : trimmed
            error.value = null
        } catch (err) {
            if (myGeneration !== generation) return

            const isAbort =
                err?.name === 'CanceledError' ||
                err?.name === 'AbortError' ||
                err?.code === 'ERR_CANCELED'
            if (isAbort) return

            const status = err?.response?.status ?? 0
            error.value = mapSearchError(status)
            products.value = []
            total.value = 0
        } finally {
            if (myGeneration === generation) {
                loading.value = false
            }
        }
    }

    return { products, total, query, loading, error, search }
}

function mapSearchError(status) {
    if (status === 404) {
        return { status: 404, message: 'This store is not available.' }
    }
    return { status, message: 'Search is unavailable right now. Please try again.' }
}
